/**
 * Guided Tour Mode — "▶ Play demo" floating button + step-by-step overlay.
 *
 * Mounts in AppShell. Visible only when:
 *   - User is logged in AND
 *   - User role is admin / manager / super_admin
 *
 * Tours are declarative arrays of steps. Each step optionally navigates to a
 * URL, waits for a target DOM element, highlights it with a glow ring + cutout,
 * and shows a positioned tooltip with the talking-point copy.
 *
 * State is persisted in localStorage so a route-change inside a tour doesn't
 * lose the user's place.
 */
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Play, X, ArrowRight, ArrowLeft, Pause, SkipForward, Sparkles } from "lucide-react";

// ────────────────────────────────────────────────────────────────────────────
// Tour catalogue
// ────────────────────────────────────────────────────────────────────────────
// Each step: { url?, target?, title, body, position?, talkingTime? }
//   url:         optional — navigate to this URL first
//   target:      CSS selector to highlight (omit for "centered" steps)
//   position:    'auto' | 'top' | 'bottom' | 'left' | 'right' | 'center'
//   talkingTime: seconds the presenter should spend (shown on the card)
const TOURS = {
  sniff: {
    id: "sniff",
    label: "5-min Sniff Test",
    description: "The fastest way to make a CFO say wow.",
    color: "amber",
    steps: [
      {
        title: "Hello there 👋",
        body: "I'll walk us through SaarOne in 5 minutes. You can pause anytime with the ⏸ button, or skip ahead. Ready?",
        position: "center", talkingTime: 10,
      },
      {
        url: "/app/setup",
        target: "[data-testid='setup-hero']",
        title: "Step 1 — Onboarding pipeline",
        body: "Every new company gets walked through 22 setup steps with auto-detection. Most cut their go-live from 14 days to 4. This is what your prospect's first hour looks like.",
        position: "bottom", talkingTime: 45,
      },
      {
        url: "/app/setup",
        target: "[data-testid='phase-strip']",
        title: "6 phases, dependency-locked",
        body: "Steps unlock in order — you can't assign CTC until you've built salary structures. The platform refuses to let admins misroute.",
        position: "bottom", talkingTime: 30,
      },
      {
        url: "/app/payroll",
        target: "[data-testid='pay-tab-structures']",
        title: "Step 2 — Salary structures",
        body: "Reusable CTC templates per grade. Build once, assign in bulk. India statutory rules built in.",
        position: "bottom", talkingTime: 25,
      },
      {
        url: "/app/payroll",
        target: "[data-testid='add-structure-btn']",
        title: "One-click structure builder",
        body: "Pick components, set formulas (% of CTC / % of Basic / Fixed / Balance / Statutory) — payslip rendering and PF/ESI/TDS computation happen automatically.",
        position: "left", talkingTime: 30,
      },
      {
        url: "/app/field/live-map",
        target: "[data-testid='section-live-map']",
        title: "Step 3 — Field Force GPS",
        body: "DPDP-compliant background GPS, auto-detected visits, anti-fraud (mock-location refused, low-accuracy flagged, teleport detected). Every field rep is a colour-coded pin.",
        position: "top", talkingTime: 45,
      },
      {
        title: "That's the 5-minute pitch.",
        body: "Onboarding, payroll, field-force — all live, all tenant-isolated, all included in ₹99 per employee per month. Want to dive deeper into any module?",
        position: "center", talkingTime: 20,
      },
    ],
  },
  standard: {
    id: "standard",
    label: "15-min Standard Demo",
    description: "The sweet spot — depth without losing them.",
    color: "indigo",
    steps: [
      {
        title: "Welcome to the standard tour 🎬",
        body: "15 minutes through onboarding, payroll, recruitment, mobile, and field force. I'll auto-advance — you talk, the prospect watches.",
        position: "center", talkingTime: 10,
      },
      {
        url: "/app/setup",
        target: "[data-testid='setup-hero']",
        title: "Setup Pipeline",
        body: "22-step onboarding flow with auto-detection and dependency locks. Industry-leading time-to-value.",
        position: "bottom", talkingTime: 45,
      },
      {
        url: "/app/payroll",
        target: "[data-testid='pay-tab-structures']",
        title: "Salary Structures",
        body: "Reusable templates. Assign by grade. Statutory components pre-seeded.",
        position: "bottom", talkingTime: 35,
      },
      {
        url: "/app/recruitment",
        target: "body",
        title: "ATS — Kanban that runs your hiring",
        body: "Drag candidates across stages. DeepSeek AI parses CVs and ranks them against the role. Magic-link onboarding wizard sends itself.",
        position: "center", talkingTime: 45,
      },
      {
        url: "/app/expenses",
        target: "body",
        title: "Expense fraud detection",
        body: "DeepSeek vision flags duplicate receipts, manipulated images, and OCR mismatches in real time. CFOs love this part.",
        position: "center", talkingTime: 35,
      },
      {
        url: "/app/field/live-map",
        target: "[data-testid='section-live-map']",
        title: "Field Force Live Map",
        body: "Auto-refreshes every 60 seconds. Open visits, fraud flags, distance tracked — all from a privacy-first DPDP-compliant pipeline.",
        position: "top", talkingTime: 40,
      },
      {
        url: "/app/field/visits",
        target: "[data-testid='section-visits']",
        title: "Auto-detected visits",
        body: "Geofence dwell ≥ 5 minutes auto-opens a visit. Photo proof and notes are captured from the mobile app.",
        position: "top", talkingTime: 35,
      },
      {
        url: "/app/workflows",
        target: "body",
        title: "Approval chains — one engine, every type",
        body: "Leave / expense / PO / loan / asset all use the same generic chain. Manager hierarchy walk + budget ceilings + amount thresholds. No code.",
        position: "center", talkingTime: 45,
      },
      {
        title: "Pricing wrap-up 💰",
        body: "₹99/employee/month. All modules included. Compare with vendors charging ₹350+ where every feature is an add-on. Questions?",
        position: "center", talkingTime: 30,
      },
    ],
  },
  field: {
    id: "field",
    label: "Field Force Deep Dive",
    description: "For prospects with field reps or distributed sales teams.",
    color: "emerald",
    steps: [
      {
        title: "Field Force Geo-Tracking 🛰️",
        body: "Built ground-up for India's DPDP compliance. I'll show you anti-fraud, auto-visits, and live map.",
        position: "center", talkingTime: 10,
      },
      {
        url: "/app/field/parties",
        target: "[data-testid='section-parties-list']",
        title: "Party Master",
        body: "Clients, sites, warehouses. Geofence radius defines the visit zone. Photo-required-on-visit toggle per party.",
        position: "top", talkingTime: 40,
      },
      {
        url: "/app/field/live-map",
        target: "[data-testid='kpi-on-duty']",
        title: "Live Map — 4 KPIs at a glance",
        body: "On-duty employees, open visits, visits today, fraud flags. CFO opens this at 11am to see who's where.",
        position: "bottom", talkingTime: 35,
      },
      {
        url: "/app/field/live-map",
        target: "[data-testid='section-live-map']",
        title: "Map with anti-fraud colour-coding",
        body: "Green = inside a visit, cyan = on duty, red = fraud flag. Mock-location apps are refused at the API level — can't even start a field day.",
        position: "top", talkingTime: 45,
      },
      {
        url: "/app/field/settings",
        target: "[data-testid='section-field-window']",
        title: "Tracking window — privacy first",
        body: "GPS is collected ONLY between configured work hours on configured days. Outside the window? Tracking stops. No employee tracked after hours, ever.",
        position: "top", talkingTime: 40,
      },
      {
        url: "/app/field/settings",
        target: "[data-testid='section-field-fraud']",
        title: "Industry-standard anti-fraud",
        body: "Mock-location refusal, accuracy floor, teleport speed floor — all configurable. Failed pings drop, day gets flagged, HR is notified.",
        position: "top", talkingTime: 35,
      },
      {
        title: "Pricing — ₹40/employee/month",
        body: "Field Force is an add-on module. ₹40 retail, ₹25 wholesale. Includes native Android app, web dashboard, and DPDP-compliant data retention (90 days).",
        position: "center", talkingTime: 25,
      },
    ],
  },
};

const COLOR_MAP = {
  amber:   { bg: "bg-amber-500",   ring: "ring-amber-300/40",   text: "text-amber-50",   chip: "bg-amber-100 text-amber-700 border-amber-200" },
  indigo:  { bg: "bg-indigo-600",  ring: "ring-indigo-300/40",  text: "text-indigo-50",  chip: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  emerald: { bg: "bg-emerald-600", ring: "ring-emerald-300/40", text: "text-emerald-50", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

// ────────────────────────────────────────────────────────────────────────────
// Tour engine
// ────────────────────────────────────────────────────────────────────────────
const LS_KEY = "saarone_active_tour";

function loadActive() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch { return null; }
}
function saveActive(v) {
  if (v == null) localStorage.removeItem(LS_KEY);
  else localStorage.setItem(LS_KEY, JSON.stringify(v));
}

export default function DemoTour() {
  const navigate = useNavigate();
  const [showLauncher, setShowLauncher] = useState(false);
  const [active, setActive] = useState(loadActive());
  const [targetRect, setTargetRect] = useState(null);
  const [paused, setPaused] = useState(false);

  const tour = active ? TOURS[active.tourId] : null;
  const step = tour ? tour.steps[active.stepIdx] : null;

  // ───────── start / stop / advance ─────────
  const startTour = useCallback((tourId) => {
    const a = { tourId, stepIdx: 0, startedAt: Date.now() };
    saveActive(a); setActive(a); setShowLauncher(false); setPaused(false);
    const t = TOURS[tourId];
    if (t.steps[0]?.url) navigate(t.steps[0].url);
  }, [navigate]);

  const stopTour = useCallback(() => {
    saveActive(null); setActive(null); setTargetRect(null);
  }, []);

  const nextStep = useCallback(() => {
    if (!tour) return;
    if (active.stepIdx >= tour.steps.length - 1) { stopTour(); return; }
    const newIdx = active.stepIdx + 1;
    const a = { ...active, stepIdx: newIdx };
    saveActive(a); setActive(a); setTargetRect(null);
    const next = tour.steps[newIdx];
    if (next?.url && next.url !== window.location.pathname) navigate(next.url);
  }, [tour, active, stopTour, navigate]);

  const prevStep = useCallback(() => {
    if (!tour || active.stepIdx === 0) return;
    const newIdx = active.stepIdx - 1;
    const a = { ...active, stepIdx: newIdx };
    saveActive(a); setActive(a); setTargetRect(null);
    const prev = tour.steps[newIdx];
    if (prev?.url && prev.url !== window.location.pathname) navigate(prev.url);
  }, [tour, active, navigate]);

  // ───────── target locator ─────────
  // Re-runs on step change and on route changes; polls briefly for late-mounted nodes
  useEffect(() => {
    if (!step) { setTargetRect(null); return; }
    if (!step.target || step.position === "center") { setTargetRect(null); return; }
    let cancelled = false; let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        // wait one frame for scroll
        requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          setTargetRect({ x: r.left, y: r.top, w: r.width, h: r.height });
        });
        return;
      }
      if (++tries < 40) setTimeout(tick, 100);
      else setTargetRect(null);  // gracefully degrade to center
    };
    tick();
    return () => { cancelled = true; };
  }, [active?.stepIdx, active?.tourId, step]);

  // ───────── auto-advance based on talkingTime (only when not paused) ─────────
  const timerRef = useRef(null);
  useEffect(() => {
    if (!step || paused) return;
    if (!step.talkingTime) return;
    timerRef.current = setTimeout(() => nextStep(), step.talkingTime * 1000);
    return () => clearTimeout(timerRef.current);
  }, [step, paused, nextStep]);

  // ───────── keyboard shortcuts ─────────
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") stopTour();
      else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); nextStep(); }
      else if (e.key === "ArrowLeft") prevStep();
      else if (e.key.toLowerCase() === "p") setPaused((p) => !p);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, nextStep, prevStep, stopTour]);

  const colors = useMemo(() => COLOR_MAP[tour?.color || "amber"], [tour]);

  // ───────── floating launcher button ─────────
  if (!active) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowLauncher(true)}
          className="group fixed bottom-5 left-5 lg:left-[276px] z-30 flex items-center gap-0 hover:gap-2 p-2.5 hover:pr-4 rounded-full bg-zinc-900/90 text-white text-xs font-medium shadow-lg opacity-70 hover:opacity-100 transition-[opacity,gap,padding] duration-200"
          aria-label="Play demo"
          data-testid="demo-launcher"
        >
          <Sparkles size={14}/>
          <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-[90px] transition-[max-width] duration-200">Play demo</span>
        </button>

        {showLauncher && (
          <div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)" }}
            onClick={() => setShowLauncher(false)}
            data-testid="demo-launcher-modal"
          >
            <div
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-600 mb-1">
                    <Sparkles size={11} className="inline mr-1"/> Guided tours
                  </div>
                  <h2 className="font-display text-2xl font-bold">Pick a demo flow</h2>
                </div>
                <button onClick={() => setShowLauncher(false)} className="p-2 hover:bg-zinc-100 rounded-lg">
                  <X size={18}/>
                </button>
              </div>
              <p className="text-sm text-zinc-500 mb-5">
                Each tour auto-advances. Pause with <kbd className="px-1.5 py-0.5 bg-zinc-100 border rounded text-xs">P</kbd>,
                step with <kbd className="px-1.5 py-0.5 bg-zinc-100 border rounded text-xs">→</kbd>,
                exit with <kbd className="px-1.5 py-0.5 bg-zinc-100 border rounded text-xs">Esc</kbd>.
              </p>
              <div className="space-y-3">
                {Object.values(TOURS).map((t) => {
                  const total = t.steps.reduce((s, x) => s + (x.talkingTime || 30), 0);
                  const mins = Math.round(total / 60);
                  const c = COLOR_MAP[t.color];
                  return (
                    <button
                      key={t.id}
                      onClick={() => startTour(t.id)}
                      className="w-full flex items-center gap-4 p-4 border border-zinc-200 rounded-xl hover:border-zinc-400 hover:shadow-sm transition-all text-left"
                      data-testid={`tour-start-${t.id}`}
                    >
                      <div className={`flex-none w-12 h-12 ${c.bg} rounded-xl grid place-items-center text-white`}>
                        <Play size={20} fill="white"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{t.label}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{t.description}</div>
                      </div>
                      <div className="text-right text-xs flex-none">
                        <div className={`inline-block px-2 py-0.5 rounded border font-mono text-[10px] ${c.chip}`}>{t.steps.length} steps</div>
                        <div className="text-zinc-400 mt-1">~ {mins} min</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t text-xs text-zinc-500 text-center">
                Need to run a manual demo instead? Click anywhere outside this panel.
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ───────── active tour overlay ─────────
  const stepCount = tour.steps.length;
  const stepNum = active.stepIdx + 1;
  const cardPos = computeCardPosition(targetRect, step?.position);
  const isCenter = !targetRect || step?.position === "center";

  return (
    <div className="fixed inset-0 z-[95] pointer-events-none" data-testid="tour-overlay">
      {/* Backdrop with cutout for highlighted element */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{
          background: targetRect ? "rgba(15,23,42,0.55)" : "rgba(15,23,42,0.78)",
          // CSS clip-path cutout
          clipPath: targetRect ? cutout(targetRect) : undefined,
        }}
      />

      {/* Glow ring around target */}
      {targetRect && (
        <div
          className={`absolute rounded-lg ring-4 ${colors.ring} transition-all duration-300`}
          style={{
            left: targetRect.x - 6, top: targetRect.y - 6,
            width: targetRect.w + 12, height: targetRect.h + 12,
            boxShadow: `0 0 0 3px ${tour.color === "emerald" ? "#34d399" : tour.color === "indigo" ? "#818cf8" : "#fbbf24"}`,
            animation: "tour-pulse 2s ease-in-out infinite",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="pointer-events-auto absolute bg-white rounded-xl shadow-2xl p-5 w-[360px] max-w-[92vw]"
        style={isCenter ? { left: "50%", top: "50%", transform: "translate(-50%, -50%)" } : cardPos}
        data-testid="tour-card"
      >
        {/* Step counter + progress bar */}
        <div className="flex items-center justify-between mb-3">
          <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${colors.bg.replace("bg-", "text-")}`}>
            {tour.label} · Step {stepNum} / {stepCount}
          </div>
          <button onClick={stopTour} className="text-zinc-400 hover:text-zinc-700" data-testid="tour-exit">
            <X size={16}/>
          </button>
        </div>
        <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden mb-4">
          <div className={`h-full ${colors.bg} transition-all duration-500`} style={{ width: `${(stepNum / stepCount) * 100}%` }}/>
        </div>

        <h3 className="font-display text-lg font-bold mb-2" data-testid="tour-title">{step.title}</h3>
        <p className="text-sm text-zinc-600 leading-relaxed" data-testid="tour-body">{step.body}</p>

        {step.talkingTime && !paused && (
          <div className="mt-3 text-[11px] text-zinc-400 font-mono">
            Auto-advancing in {step.talkingTime}s · press <kbd className="px-1 bg-zinc-100 rounded">P</kbd> to pause
          </div>
        )}

        <div className="flex items-center justify-between mt-5 pt-4 border-t">
          <div className="flex items-center gap-2">
            <button
              onClick={prevStep}
              disabled={active.stepIdx === 0}
              className="p-2 hover:bg-zinc-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="tour-prev"
            >
              <ArrowLeft size={16}/>
            </button>
            <button
              onClick={() => setPaused((p) => !p)}
              className={`p-2 rounded-lg ${paused ? "bg-amber-100 text-amber-700" : "hover:bg-zinc-100"}`}
              data-testid="tour-pause"
            >
              {paused ? <Play size={16}/> : <Pause size={16}/>}
            </button>
          </div>
          <button
            onClick={nextStep}
            className={`px-4 py-2 ${colors.bg} text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:opacity-90`}
            data-testid="tour-next"
          >
            {active.stepIdx === stepCount - 1 ? "Finish" : "Next"}
            {active.stepIdx === stepCount - 1 ? null : <ArrowRight size={14}/>}
          </button>
        </div>

        {active.stepIdx < stepCount - 1 && (
          <button
            onClick={stopTour}
            className="absolute -bottom-9 left-1/2 -translate-x-1/2 text-xs text-white/70 hover:text-white pointer-events-auto flex items-center gap-1"
            data-testid="tour-stop"
          >
            <SkipForward size={11}/> Skip to manual demo
          </button>
        )}
      </div>

      <style>{`
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 3px currentColor, 0 0 0 0 currentColor; }
          50%      { box-shadow: 0 0 0 3px currentColor, 0 0 0 12px transparent; }
        }
      `}</style>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
function cutout({ x, y, w, h }) {
  // Polygon that covers the viewport with a rectangular hole over the target.
  const pad = 6;
  const x1 = x - pad, y1 = y - pad, x2 = x + w + pad, y2 = y + h + pad;
  return `polygon(
    0 0, 100% 0, 100% 100%, 0 100%, 0 0,
    ${x1}px ${y1}px,
    ${x1}px ${y2}px,
    ${x2}px ${y2}px,
    ${x2}px ${y1}px,
    ${x1}px ${y1}px
  )`;
}

function computeCardPosition(rect, position) {
  if (!rect) return null;
  const CARD_W = 360, CARD_H = 240, PAD = 16;
  const vw = window.innerWidth, vh = window.innerHeight;

  let pos = position;
  if (!pos || pos === "auto") {
    // Auto-pick the side with the most room.
    const spaces = {
      bottom: vh - (rect.y + rect.h),
      top: rect.y,
      right: vw - (rect.x + rect.w),
      left: rect.x,
    };
    pos = Object.entries(spaces).sort((a, b) => b[1] - a[1])[0][0];
  }

  let left, top;
  if (pos === "bottom") { left = rect.x + rect.w / 2 - CARD_W / 2; top = rect.y + rect.h + PAD; }
  else if (pos === "top")   { left = rect.x + rect.w / 2 - CARD_W / 2; top = rect.y - CARD_H - PAD; }
  else if (pos === "right") { left = rect.x + rect.w + PAD; top = rect.y + rect.h / 2 - CARD_H / 2; }
  else /* left */           { left = rect.x - CARD_W - PAD; top = rect.y + rect.h / 2 - CARD_H / 2; }

  // Clamp to viewport
  left = Math.max(PAD, Math.min(left, vw - CARD_W - PAD));
  top  = Math.max(PAD, Math.min(top,  vh - CARD_H - PAD));
  return { left, top };
}
